import { useState } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { analyticsAccountType, removalMethod } from './accountAnalytics'
import { AccountsError, transactionCountOf, useDeleteAccount, type Account } from './useAccounts'

/**
 * The two-phase delete flow — DELETE without the flag, catch a manual
 * account's 409, read `transaction_count`, re-confirm with
 * `?confirm_delete_transactions=true` — extracted out of `Accounts.tsx`
 * (issue #396, carried over from #395) so Ticket 2's `AccountDetail.tsx` gets
 * the SAME implementation rather than growing its own copy. The 409 path —
 * the one carrying a real number into a destructive confirmation — is the
 * piece most likely to drift between two hand-written copies.
 *
 * One instance per screen that offers delete; `target` is that screen's own
 * `ConfirmDialog`'s `open`/copy source, and `confirm` is its `onConfirm`.
 *
 * `onDeleted` fires once, after a delete that actually succeeded (204, not a
 * 409 that only flipped the confirm flag) and before the dialog closes —
 * a caller's toast-and-close (`Accounts.tsx`) or toast-and-navigate
 * (`AccountDetail.tsx`) both need the account that was just removed, and
 * this is the one place either can read it back.
 */
export type AccountDeleteFlow = {
  target: Account | null
  start: (account: Account) => void
  cancel: () => void
  confirm: () => Promise<void>
  isPending: boolean
  bodyFor: (target: Account) => string
  errorFor: (thrown: unknown) => string
}

/**
 * The ONE place "linked vs manual" becomes "hide vs delete" wording. The
 * endpoint is `DELETE /accounts/{id}` either way, but it only sets
 * `is_hidden` for a linked account (any `provider` other than `'manual'`) and
 * really removes a manual one. Every control in the flow — the row action, the
 * dialog title, the confirm button, the success toast, the analytics `mode` —
 * reads from here, so `/accounts` and `/accounts/:id` cannot disagree.
 * `accountDeleteBody` and `accountDeleteErrorFor` below key off the same
 * `removalMethod` test (`accountAnalytics.ts`, the one copy of the manual check) for the dialog body and failure copy.
 */
export function accountRemovalCopy(account: Account): {
  action: string
  title: string
  confirmLabel: string
  toast: string
  mode: 'hide_account' | 'delete_account'
} {
  if (removalMethod(account) === 'hide') {
    return {
      action: 'Hide account',
      title: `Hide ${account.name}?`,
      confirmLabel: 'Hide account',
      toast: `${account.name} hidden`,
      mode: 'hide_account',
    }
  }
  return {
    action: 'Delete',
    title: `Delete ${account.name}?`,
    confirmLabel: 'Delete',
    toast: `${account.name} deleted`,
    mode: 'delete_account',
  }
}

/**
 * What `ConfirmDialog`'s body says BEFORE the request — M5-R19. A linked
 * account is never "deleted", only hidden (Plaid cannot unlink one account of
 * an Item), and saying so up front is what stops a user from later catching
 * the app in an apparent lie when it resyncs.
 */
export function accountDeleteBody(target: Account): string {
  if (removalMethod(target) === 'hide') {
    return "TrackE5 can't unlink one account of a connected bank — this will hide the account instead of deleting it."
  }
  return `Delete ${target.name}? This cannot be undone.`
}

/**
 * What `ConfirmDialog` shows on rejection. `null` from `transactionCountOf` —
 * a 409 whose body is not the documented shape — gets the generic message
 * rather than a confident, fabricated "0 transactions" (architect review,
 * issue #395 non-blocking).
 */
export function accountDeleteErrorFor(thrown: unknown, target: Account | null = null): string {
  if (thrown instanceof AccountsError && thrown.status === 409) {
    const count = transactionCountOf(thrown.body)
    if (count !== null) {
      return `This account has ${count} transaction${count === 1 ? '' : 's'}. Press Delete again to remove ${count === 1 ? 'it' : 'them'} too.`
    }
  }
  // A linked account is hidden, never deleted (see `accountDeleteBody`), so
  // the failure copy says "hidden", matching the title and confirm label
  // `accountRemovalCopy` gives it on both screens.
  if (target !== null && removalMethod(target) === 'hide') return 'The account could not be hidden.'
  return 'The account could not be deleted.'
}

export function useAccountDeleteFlow(onDeleted: (account: Account) => void): AccountDeleteFlow {
  // Two states rather than a discriminated union: `ConfirmDialog` already
  // owns the pending/failed transition and re-renders `errorFor`'s string on
  // the rejection that sets this, so nothing here needs to duplicate that.
  const [target, setTarget] = useState<Account | null>(null)
  const [needsConfirmFlag, setNeedsConfirmFlag] = useState(false)
  const del = useDeleteAccount()

  function cancel() {
    setTarget(null)
    setNeedsConfirmFlag(false)
  }

  async function confirm() {
    if (target === null) return
    try {
      await del.remove(target.id, needsConfirmFlag)
      // `account_removed` — on a REAL removal only (204), never on the 409
      // that merely flips `needsConfirmFlag` for a retry below. `method` is
      // `hide` for a linked account and `delete` for a manual one (the same
      // split `accountRemovalCopy` uses); `account_type` is the closed iOS set
      // (`analyticsAccountType`). Counts and enums only — no name, mask or id.
      emit('account_removed', {
        account_type: analyticsAccountType(target),
        method: removalMethod(target),
      })
      onDeleted(target)
      cancel()
    } catch (cause) {
      // A manual account with live transactions answers the first attempt
      // (no flag) with 409 — flip the flag so the SAME button's next press
      // retries with it, and let the rejection surface through `errorFor`
      // rather than closing here.
      if (cause instanceof AccountsError && cause.status === 409) setNeedsConfirmFlag(true)
      throw cause
    }
  }

  return {
    target,
    start: setTarget,
    cancel,
    confirm,
    isPending: del.isPending,
    bodyFor: accountDeleteBody,
    errorFor: (thrown) => accountDeleteErrorFor(thrown, target),
  }
}
